import React, {
  ReactElement,
} from 'react';
import isNil from 'lodash/isNil';
import {
  Area,
  CartesianGrid,
  ComposedChart,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

const DEFAULT_TREND_CHART_COLORS = {
  grid: '#E5E7EB',
  tick: '#6B7280',
  cursor: '#CBD5E1',
  reference: '#9CA3AF',
} as const;

type TrendSeries = {
  key: string;
  color: string;
};

type TrendChartProps = {
  series: TrendSeries[];
  data: Array<Record<string, string | number | undefined>>;
  emptyText: string;
  xKey?: string;
  axes?: boolean;
  fillArea?: boolean;
  formatYTick?: (value: number) => string;
  tooltip?: ReactElement;
  yDomain?: [number, number];
  yTicks?: number[];
  yAxisWidth?: number;
  referenceLineY?: number;
  reversedYAxis?: boolean;
  height?: number;
  gridStroke?: string;
  tickFill?: string;
  cursorStroke?: string;
  referenceLineStroke?: string;
};

export default function TrendChart({
  series,
  data,
  emptyText,
  xKey = 'fullLabel',
  axes = false,
  fillArea = false,
  formatYTick,
  tooltip,
  yDomain,
  yTicks,
  yAxisWidth,
  referenceLineY,
  reversedYAxis = false,
  height = 220,
  gridStroke = DEFAULT_TREND_CHART_COLORS.grid,
  tickFill = DEFAULT_TREND_CHART_COLORS.tick,
  cursorStroke = DEFAULT_TREND_CHART_COLORS.cursor,
  referenceLineStroke = DEFAULT_TREND_CHART_COLORS.reference,
}: TrendChartProps) {
  const primarySeries = series[0];
  const gradientId = `trend-${primarySeries.key}`;
  const primaryKey = primarySeries.key;
  const primaryColor = primarySeries.color;
  const singleSeries = series.length === 1;
  const chartKey = series.map((seriesItem) => seriesItem.key).join('|');
  const showAreaFill = fillArea && singleSeries;
  const resolvedYAxisWidth = yAxisWidth ?? (formatYTick ? 112 : 34);

  return data.length === 0 ? (
    <div className="flex h-full items-center justify-center rounded-md border border-dashed border-gray-200 bg-white text-sm text-gray-500">
      {emptyText}
    </div>
  ) : (
    <div key={chartKey} style={{ width: '100%', height }}>
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart
          data={data}
          margin={{
            top: axes ? 12 : 6,
            right: axes ? 8 : 2,
            bottom: axes ? 4 : 0,
            left: 0,
          }}
        >
          {showAreaFill && (
            <defs>
              <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={`${primaryColor}cc`} />
                <stop offset="100%" stopColor={`${primaryColor}08`} />
              </linearGradient>
            </defs>
          )}
          {axes && <CartesianGrid vertical={false} stroke={gridStroke} />}
          {tooltip && (
            <Tooltip
              cursor={axes ? { stroke: cursorStroke, strokeWidth: 1 } : false}
              content={tooltip}
            />
          )}
          {axes && (
            <>
              <XAxis
                dataKey={xKey}
                axisLine={false}
                tickLine={false}
                minTickGap={56}
                tick={{ fill: tickFill, fontSize: 11 }}
                tickFormatter={(value) => String(value).split(' ')[0]}
              />
              <YAxis
                domain={yDomain}
                ticks={yTicks}
                reversed={reversedYAxis}
                axisLine={false}
                tickLine={false}
                width={resolvedYAxisWidth}
                tick={{ fill: tickFill, fontSize: 11 }}
                tickFormatter={(value) => (
                  formatYTick
                    ? formatYTick(Number(value))
                    : String(value)
                )}
              />
            </>
          )}
          {!isNil(referenceLineY) && (
            <ReferenceLine
              y={referenceLineY}
              stroke={referenceLineStroke}
              strokeDasharray="4 4"
            />
          )}
          {showAreaFill && (
            <Area
              key={primaryKey}
              type="linear"
              dataKey={primaryKey}
              stroke="none"
              fill={`url(#${gradientId})`}
              fillOpacity={1}
              dot={false}
              activeDot={false}
              isAnimationActive={false}
            />
          )}
          {series.map(({ key, color }) => (
            <Line
              key={key}
              type="linear"
              dataKey={key}
              stroke={color}
              strokeWidth={2.5}
              dot={{
                r: 2.5,
                fill: color,
                stroke: color,
                strokeWidth: 1.5,
              }}
              activeDot={{
                r: 4,
                fill: color,
                stroke: color,
                strokeWidth: 2,
              }}
            />
          ))}
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}
